import { Box, Paper, Typography, Stack, Divider, FormActionGroup } from '@ace-clublabs/shared-component-library';
import { useGlobalState } from '../../../state/state';
import { BenefitLevelPriceCartItem } from './ManageMembershipPriceCartItems/BenefitLevel';
import { FamilyMemberPriceCartItem } from './ManageMembershipPriceCartItems/FamilyMembers';
import { AddOnsPriceCartItem } from './ManageMembershipPriceCartItems/AddOns';
import { stringHelper } from '../../../utils/stringHelper';
import { useState } from 'react';
import { BackButtonConfirmModal } from '../../BackButtonConfirmModal/BackButtonConfirmModal';
import { useHistory } from 'react-router-dom';
import { Pages } from '../../../utils/constants';
import { navigateToMembershipBillPay } from '../../../utils/navigateToMembershipBillPay';
import { getIsMembershipChanging } from '../../../state/helpers';
import { SalesTaxPriceCartItem } from './ManageMembershipPriceCartItems/SalesTax';
import { AdmissionFeePriceCartItem } from './ManageMembershipPriceCartItems/AdmissionFee';
import { PromotionsPriceCartItem } from './ManageMembershipPriceCartItems/Promotions';
import { DiscountsCartItem } from './ManageMembershipPriceCartItems/Discounts';

export const ManageMembershipPriceCart = () => {
  const history = useHistory();
  const [showBackButtonModal, setShowBackButtonModal] = useState(false);
  const [membershipDetails] = useGlobalState('memberDetails');
  const [formData] = useGlobalState('formData');
  const [priceCart] = useGlobalState('priceCart');
  const [loading] = useGlobalState('loading');
  const [tags] = useGlobalState('tags');
  const { inRenewal, inArrears } = membershipDetails;
  const [prepaymentLoading, setPrepaymentLoading] = useState(false);

  const isChangingMembership = getIsMembershipChanging(membershipDetails, formData);

  const onCancelClick = () => {
    if (!isChangingMembership) {
      history.push(Pages.HomePage);
    } else {
      setShowBackButtonModal(true);
    }
  };
  const displayPriceDetailsOnLoad = (inRenewal || inArrears) && priceCart.total! > 0;
  const isPriceCartLoading = loading.priceCart;

  const handlePaymentClick = async () => {
    setPrepaymentLoading(true);
    await navigateToMembershipBillPay(membershipDetails, tags, history);
    setPrepaymentLoading(false);
  };

  const isLoading = isPriceCartLoading || prepaymentLoading;
  const submitButtonDisabled = (!displayPriceDetailsOnLoad && !isChangingMembership) || isLoading;

  return (
    <Box
      className="manage-membership-price-cart-section"
      sx={{ marginTop: { xs: '1.5rem', sm: '1.5rem', md: '4.5rem', lg: '4.5rem' } }}
    >
      <BackButtonConfirmModal
        visible={showBackButtonModal}
        onConfirm={() => {
          setShowBackButtonModal(false);
          history.push(Pages.HomePage);
        }}
        onCancel={() => setShowBackButtonModal(false)}
      />
      <Paper
        variant="elevation"
        elevation={8}
        sx={{
          width: '100% !important',
          padding: '1rem',
        }}
      >
        <Typography variant="b1Bold">Summary</Typography>
        {isChangingMembership || displayPriceDetailsOnLoad ? (
          <Box className="manage-membership-price-summary-details" sx={{ paddingTop: '1rem' }}>
            <BenefitLevelPriceCartItem />
            <FamilyMemberPriceCartItem />
            <AddOnsPriceCartItem />
            <PromotionsPriceCartItem />
            <AdmissionFeePriceCartItem />
            <SalesTaxPriceCartItem />
            <Divider sx={{ marginBottom: '1rem' }} />
            <Stack direction={'row'} className="price-cart-total" sx={{ justifyContent: 'space-between' }}>
              <Typography variant="b1Bold">Total</Typography>
              <Typography variant="b1Bold">{stringHelper.formatMoney(priceCart?.total!.toString())}</Typography>
            </Stack>
            <DiscountsCartItem />
          </Box>
        ) : (
          <Typography
            variant="b1Regular"
            sx={{ color: '#707070', marginTop: '.5rem', marginBottom: '1rem' }}
            className="manage-membership-empty-cart-message"
          >
            {emptyCartMessage}
          </Typography>
        )}
        <FormActionGroup
          fixed={{ xs: true, sm: true, md: false, lg: false }}
          sx={{
            marginTop: '1rem',
          }}
          secondaryContent={
            <Stack
              direction={'row'}
              className="price-cart-mobile-total"
              sx={{
                justifyContent: 'space-between',
                marginBottom: '1rem',
              }}
            >
              <Typography variant="b1Bold">Total:</Typography>
              <Typography variant="b1Bold">{stringHelper.formatMoney(priceCart?.total!.toString())}</Typography>
            </Stack>
          }
          slotProps={{
            primaryButton: {
              label: isPriceCartLoading ? 'Calculating' : isChangingMembership ? 'Submit change' : 'Pay now',
              disabled: submitButtonDisabled,
              onClick: handlePaymentClick,
              className: `${isLoading ? 'loading' : ''}  manage-membership-submit-change-cta`,
              loading: isLoading,
              sx: { '&.loading': { backgroundColor: '#1c4da6 !important', borderColor: '#1c4da6 !important' } },
            },
            secondaryButton: {
              variant: 'outlined',
              label: 'Cancel',
              className: 'manage-membership-cancel-cta',
              onClick: onCancelClick,
              startIcon: undefined,
            },
            secondaryContentGrid: {
              visible: { xs: true, sm: true, md: false, lg: false },
            },
            actionsGrid: {
              gap: '0.5rem',
              justifyContent: 'space-between',
            },
            primaryButtonGrid: {
              order: { xs: 2, sm: 2, md: 1, lg: 1 },
              size: {
                xs: 6,
                sm: 6,
                md: 12,
                lg: 12,
              },
            },
            secondaryButtonGrid: {
              order: { xs: 1, sm: 1, md: 2, lg: 2 },
              size: {
                xs: 6,
                sm: 6,
                md: 12,
                lg: 12,
              },
              visible: { xs: true, sm: true, md: true, lg: true },
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export const emptyCartMessage =
  'A summary of your updates with pricing details will appear after you’ve made changes to your membership.';
